/**
 * Tool Registry Hook
 * Fetches and manages the list of available tools
 * 
 * Permission Model:
 * - Portal.Admin: Access to ALL tools
 * - Portal.User: Access to portal shell only, individual tools must be explicitly granted
 * - Tool access is stored in Cosmos DB via Permission Service
 */
import { useQuery } from "@tanstack/react-query";
import { useMsal } from "@azure/msal-react";

import type { Tool, ToolRegistryResponse } from "./types";
import useApiClient from "../hooks/useApiClient";
import { useDevBypass, devAccount, getUserRoles } from "../utils/devBypass";
import { getConfig } from "../config";

// Default tools for local development / fallback
const defaultTools: Tool[] = [
  {
    id: "client-map",
    name: "Client Map",
    description: "View and manage client locations on an interactive map",
    type: "embedded",
    category: "Sales",
    icon: "Location",
    component: "ClientMap",
  },
  {
    id: "cad-analysis",
    name: "Portal & CAD Agent",
    description: "AI-powered portal analysis and reports",
    type: "embedded",
    category: "Analysis",
    icon: "DocumentTableSearch",
    component: "CADAnalysis",
  },
  {
    id: "crm",
    name: "CRM Assistant",
    description: "AI-powered CRM assistant for contacts, deals, and pipeline management",
    type: "embedded",
    category: "Sales",
    icon: "People",
    component: "CRM",
  },
  {
    id: "sharepoint-agent",
    name: "SharePoint Q&A",
    description: "AI-powered search and Q&A over LensLock SharePoint content",
    type: "embedded",
    category: "Operations",
    icon: "DocumentSearch",
    component: "SharePointQA",
  },
  {
    id: "finance",
    name: "Finance",
    description: "Finance assistant and pricing / COGS",
    type: "embedded",
    category: "Operations",
    icon: "Money",
    component: "Finance",
  },
  {
    id: "jira-agent",
    name: "JIRA Agent",
    description: "AI agent that queries and updates JIRA projects and tickets",
    type: "embedded",
    category: "Analysis",
    icon: "TicketDiagonal",
    component: "JiraAssistant",
  },
  {
    id: "executive-dashboard",
    name: "Executive Dashboard",
    description: "Business health at a glance with drill-through to the agents",
    type: "embedded",
    category: "Executive",
    icon: "DataTrending",
    component: "ExecutiveDashboard",
  },
  {
    id: "conferences",
    name: "Conferences & Shows",
    description: "Coordinate trade shows & conferences — deadlines, team, travel, and nudges",
    type: "embedded",
    category: "Sales",
    icon: "CalendarLtr",
    component: "Conferences",
  },
];

interface UserPermissions {
  userId: string;
  permissions: Record<string, string[]>;
}

interface UseToolRegistryReturn {
  tools: Tool[];
  accessibleTools: Tool[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * useToolRegistry Hook
 * Fetches available tools from the backend and filters by user permissions
 */
export function useToolRegistry(): UseToolRegistryReturn {
  const api = useApiClient();
  const { accounts } = useMsal();
  
  // Get the current account - use dev mock or real MSAL account
  const account = useDevBypass ? devAccount : accounts[0];
  const userId = account?.localAccountId || "";
  
  // Get user roles from ID token claims (or dev mode defaults)
  const roles = getUserRoles(account);
  const isAdmin = roles.includes("Portal.Admin");

  // Fetch tools from API
  const {
    data: tools = defaultTools,
    isLoading: toolsLoading,
    error: toolsError,
    refetch: refetchTools,
  } = useQuery<Tool[]>({
    queryKey: ["tool-registry"],
    queryFn: async () => {
      try {
        const data = await api.get<ToolRegistryResponse>("/tools");
        return data.tools?.length > 0 ? data.tools : defaultTools;
      } catch (err) {
        console.warn("Failed to fetch tools from API, using defaults:", err);
        return defaultTools;
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    // Always enable in dev bypass mode
    enabled: useDevBypass || !!account,
  });

  // Fetch user permissions (only if not admin - admins have all access)
  const {
    data: userPermissions,
    isLoading: permsLoading,
  } = useQuery<UserPermissions>({
    queryKey: ["user-permissions", userId],
    queryFn: async () => {
      try {
        const data = await api.get<UserPermissions>("/permissions/user/me");
        return data;
      } catch (err) {
        console.warn("Failed to fetch permissions:", err);
        // Return empty permissions - user won't have access to any tools
        return { userId: userId || "", permissions: {} };
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userId && !isAdmin, // Skip for admins (and dev bypass is admin)
  });

  // Filter tools by user permissions
  const accessibleTools = tools.filter((tool) => {
    // Portal.Admin has access to ALL tools (including dev bypass mode)
    if (isAdmin) {
      return true;
    }
    
    const toolPerms = userPermissions?.permissions?.[tool.id] ?? [];

    // Granular tools pair permissions by the _user / _admin suffix convention
    // (same rule as granularPermsForTool in AdminPage.tsx). Holding either
    // grants the tool. Previously this was hardcoded to `finance`, which meant
    // any other granular tool silently fell back to the plain `access` check —
    // and a CRM user holding crm_assistant_* but not `access` could not see the
    // tool at all.
    const granular = (tool.available_permissions ?? []).filter(
      (p) => p.endsWith("_user") || p.endsWith("_admin"),
    );
    if (granular.length > 0) {
      return granular.some((p) => toolPerms.includes(p));
    }
    return toolPerms.includes("access");
  });

  // Conferences & Shows is photondev-only during build-out — hide it entirely
  // unless the runtime config flag is on (mirrors the Calls two-gate pattern),
  // regardless of admin status or what the backend tool list returns.
  const conferencesOn = getConfig().enableConferences;
  const gateConferences = (list: Tool[]): Tool[] =>
    conferencesOn ? list : list.filter((t) => t.id !== "conferences");

  return {
    tools: gateConferences(tools),
    accessibleTools: gateConferences(accessibleTools),
    isLoading: toolsLoading || permsLoading,
    error: toolsError as Error | null,
    refetch: refetchTools,
  };
}

export default useToolRegistry;
